import { Check, Info } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfirm } from "@/hooks/use-confirm";
import type { SubscriptionDetails } from "@/lib/api/billing";
import {
  useCancelSubscription,
  useResumeSubscription,
  useSyncSubscription,
  useUpdatePaymentMethod,
} from "@/lib/hooks/use-billing";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./billing-content.i18n";
import { CheckoutDialog } from "./checkout-dialog";
import { PaymentInfo } from "./payment-info";
import type { TenantTier, TierInfo } from "./types";

type TierConfig = {
  name: string;
  price: string;
  minLicenses: number;
  features: string[];
};

type BillingContentProps = {
  tierInfo: TierInfo & { tier: TenantTier };
  subscription: SubscriptionDetails | null;
  isTestMode: boolean;
};

export function BillingContent({ tierInfo, subscription, isTestMode }: BillingContentProps) {
  const [checkoutTier, setCheckoutTier] = useState<TenantTier | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const [searchParams] = useSearchParams();
  const t = useT(T);

  const cancelMutation = useCancelSubscription();
  const resumeMutation = useResumeSubscription();
  const syncMutation = useSyncSubscription();
  const updatePaymentMutation = useUpdatePaymentMethod();

  const isPending =
    cancelMutation.isPending || resumeMutation.isPending || updatePaymentMutation.isPending;

  // Auto-sync when returning from Mollie checkout
  useEffect(() => {
    const cardUpdated = searchParams.get("card_updated");
    const checkoutComplete = searchParams.get("checkout");

    if (cardUpdated === "true" || checkoutComplete === "complete") {
      syncMutation
        .mutateAsync()
        .catch(() => {
          // Sync failure is non-fatal — the page still renders the last known
          // billing state; the query refetch will retry on next mount.
        })
        .finally(() => {
          window.history.replaceState({}, "", "/settings/billing");
        });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const tiers: Record<TenantTier, TierConfig> = {
    basic: {
      name: t.basicName,
      price: t.basicPrice,
      minLicenses: 3,
      features: [t.basicFeature1, t.basicFeature2, t.basicFeature3, t.basicFeature4],
    },
    business: {
      name: t.businessName,
      price: t.businessPrice,
      minLicenses: 10,
      features: [
        t.businessFeature1,
        t.businessFeature2,
        t.businessFeature3,
        t.businessFeature4,
        t.businessFeature5,
        t.businessFeature6,
      ],
    },
    enterprise: {
      name: t.enterpriseName,
      price: t.enterprisePrice,
      minLicenses: 25,
      features: [
        t.enterpriseFeature1,
        t.enterpriseFeature2,
        t.enterpriseFeature3,
        t.enterpriseFeature4,
        t.enterpriseFeature5,
        t.enterpriseFeature6,
      ],
    },
  };

  const tierOrder: TenantTier[] = ["basic", "business", "enterprise"];
  const hasActiveSubscription = subscription?.status === "active";
  const isCancelled = subscription?.status === "cancelled" || subscription?.cancelAtPeriodEnd;

  const handleCancelSubscription = async () => {
    const confirmed = await confirm({
      title: t.cancelSubscription,
      description: t.cancelConfirm,
      confirmText: t.cancelSubscription,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    await cancelMutation.mutateAsync();
  };

  const handleResumeSubscription = async () => {
    await resumeMutation.mutateAsync();
  };

  const handleChangeCard = async () => {
    const result = await updatePaymentMutation.mutateAsync();
    if (result.checkoutUrl) {
      window.location.href = result.checkoutUrl;
    }
  };

  return (
    <div className="space-y-6">
      {/* Test mode banner */}
      {isTestMode && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
          <h3 className="font-semibold text-foreground">{t.testModeTitle}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t.testModeDescription}</p>
          <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
            <div className="rounded bg-background/60 p-2 font-mono">
              <div className="text-xs text-muted-foreground">{t.testCardVisa}</div>
              <div className="text-foreground">4543 4740 0224 9996</div>
            </div>
            <div className="rounded bg-background/60 p-2 font-mono">
              <div className="text-xs text-muted-foreground">{t.testCardMastercard}</div>
              <div className="text-foreground">2223 0000 1047 9399</div>
            </div>
            <div className="rounded bg-background/60 p-2 font-mono">
              <div className="text-xs text-muted-foreground">{t.testCardAmex}</div>
              <div className="text-foreground">3782 822463 10005</div>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t.testCardExpiry} · {t.testCardCvc}
          </p>
        </div>
      )}

      {/* Payment info */}
      {subscription && (
        <PaymentInfo
          subscription={subscription}
          t={{
            paymentMethod: t.paymentMethod,
            noPaymentMethod: t.noPaymentMethod,
            expires: t.expires,
            active: t.active,
            pending: t.pending,
            cancelled: t.cancelled,
            pastDue: t.pastDue,
            noSubscription: t.noSubscription,
            cancelsOn: t.cancelsOn,
          }}
        />
      )}

      {/* Current plan summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t.currentPlan}
            <Badge variant="secondary">{tiers[tierInfo.tier].name}</Badge>
            {subscription?.seats && (
              <span className="text-sm font-normal text-muted-foreground">
                ({subscription.seats} {t.seats.toLowerCase()})
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {tierInfo.name} - {tierInfo.userCount} {t.users}
          </CardDescription>
        </CardHeader>
        {(hasActiveSubscription || isCancelled) && subscription && (
          <CardFooter className="flex gap-2">
            {isCancelled ? (
              <Button
                variant="default"
                size="sm"
                onClick={handleResumeSubscription}
                loading={isPending}
              >
                {t.resumeSubscription}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelSubscription}
                loading={isPending}
              >
                {t.cancelSubscription}
              </Button>
            )}
            {subscription.paymentMethod && (
              <Button variant="outline" size="sm" onClick={handleChangeCard} disabled={isPending}>
                {t.changeCard}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      {/* Metered usage note */}
      <Card className="border-muted bg-muted/30">
        <CardContent className="flex items-start gap-3 pt-4 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {t.aiUsageNote}{" "}
            <Link
              to="/settings/usage"
              className="underline underline-offset-2 hover:text-foreground"
            >
              {t.aiUsageLink}
            </Link>
            .
          </p>
        </CardContent>
      </Card>

      {/* Tier cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {tierOrder.map((tier) => {
          const config = tiers[tier];
          const isCurrent = tier === tierInfo.tier;

          return (
            <Card key={tier} className={isCurrent ? "border-primary ring-2 ring-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{config.name}</CardTitle>
                  {isCurrent && <Badge>{t.current}</Badge>}
                </div>
                <CardDescription>
                  {tier === "enterprise" ? (
                    t.contactSales
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-foreground">{config.price}</span>{" "}
                      {t.perUserMonth}
                    </>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-muted-foreground">
                  {t.minLicenses}: {config.minLicenses}
                </p>
                <ul className="space-y-2">
                  {config.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {tier === "enterprise" ? (
                  <Button className="w-full" variant="outline" disabled>
                    {t.contactSales}
                  </Button>
                ) : isCurrent && hasActiveSubscription ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => setCheckoutTier(tier)}
                  >
                    {t.changeSeats}
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => setCheckoutTier(tier)}>
                    {hasActiveSubscription ? t.changePlan : t.subscribe}
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Confirmation dialog */}
      <ConfirmationDialog />

      {/* Checkout dialog */}
      {checkoutTier && (
        <CheckoutDialog
          open={checkoutTier !== null}
          onClose={() => setCheckoutTier(null)}
          targetTier={checkoutTier}
          currentSubscription={subscription}
          t={{
            subscribeTo: t.subscribeTo,
            changeTo: t.changeTo,
            seats: t.seats,
            removeSeat: t.removeSeat,
            addSeat: t.addSeat,
            minSeats: t.minSeats,
            monthlyTotal: t.monthlyTotal,
            dueToday: t.dueToday,
            startingNextMonth: t.startingNextMonth,
            cancel: t.cancel,
            proceedToPayment: t.proceedToPayment,
            confirmChange: t.confirmChange,
            processing: t.processing,
          }}
        />
      )}
    </div>
  );
}
